import React, { useEffect, useRef, useId } from "react";
import PhoneInputWithCountry from "react-phone-number-input";
import {
  isPossiblePhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./PhoneInput.css";
import { useOrderPersonalization } from "@/shared/hooks/useOrderPersonalization";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  country?: string; // Pays forcé pour désactiver la détection auto
  onCountryChange?: (country: string) => void;
  personalization?: {
    input?: {
      background?: string;
      border?: string;
      focusBorder?: string;
      focusRing?: string;
      text?: string;
      placeholder?: string;
      borderRadius?: string;
    };
    text?: {
      primary?: string;
      secondary?: string;
    };
  };
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  error,
  placeholder = "06 12 34 56 78",
  required = false,
  disabled = false,
  onValidationChange,
  country,
  onCountryChange,
  personalization,
}) => {
  const handleChange = (phoneValue: string | undefined) => {
    const newValue = phoneValue || "";
    onChange(newValue);

    // Validate and notify parent component
    if (onValidationChange) {
      const isValid = newValue ? isPossiblePhoneNumber(newValue) : !required;
      onValidationChange(isValid);
    }
  };

  const isValid = value ? isPossiblePhoneNumber(value) : !required;
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId().replace(/:/g, '-');

  // Utiliser useOrderPersonalization comme fallback si personalization n'est pas fourni
  // Gérer le cas où useOrderPersonalization n'est pas disponible (pas dans PersonalizationProvider)
  let orderPersonalization;
  try {
    orderPersonalization = useOrderPersonalization();
  } catch (e) {
    // Si le hook n'est pas disponible, utiliser des valeurs par défaut
    orderPersonalization = null;
  }

  // Utiliser personalizationProp si fourni, sinon utiliser orderPersonalization, sinon valeurs par défaut
  const effectivePersonalization = personalization || (orderPersonalization ? {
    input: {
      background: orderPersonalization.input.background,
      border: orderPersonalization.input.border,
      focusBorder: orderPersonalization.input.focusBorder,
      focusRing: orderPersonalization.input.focusRing,
      text: orderPersonalization.input.text,
      placeholder: orderPersonalization.input.placeholder,
      borderRadius: orderPersonalization.input.borderRadius,
    },
    text: {
      primary: orderPersonalization.text.primary,
      secondary: orderPersonalization.text.secondary,
    },
  } : undefined);

  // Styles de personnalisation - utiliser les valeurs fournies ou les valeurs par défaut du thème
  // Les valeurs par défaut sont basées sur le thème standard mais peuvent être surchargées
  const inputBg = effectivePersonalization?.input?.background || '#FFFFFF';
  const inputBorder = error ? '#DC2626' : (effectivePersonalization?.input?.border || '#D1D5DB');
  const inputText = effectivePersonalization?.input?.text || '#111827';
  const inputPlaceholder = effectivePersonalization?.input?.placeholder || '#9CA3AF';
  const inputBorderRadius = effectivePersonalization?.input?.borderRadius || 'rounded-2xl';
  const focusBorder = effectivePersonalization?.input?.focusBorder || '#9333EA';
  const focusRing = effectivePersonalization?.input?.focusRing || '#9333EA';
  const textSecondary = effectivePersonalization?.text?.secondary || '#6B7280';

  // Convertir les classes Tailwind en styles CSS si nécessaire
  const getBorderRadiusStyle = () => {
    // Si c'est déjà une valeur CSS (px, rem, %, etc.), la retourner telle quelle
    if (inputBorderRadius && (inputBorderRadius.includes('px') || inputBorderRadius.includes('rem') || inputBorderRadius.includes('%') || inputBorderRadius.includes('em'))) {
      return inputBorderRadius;
    }
    // Sinon, convertir les classes Tailwind
    if (inputBorderRadius && inputBorderRadius.includes('rounded')) {
      if (inputBorderRadius === 'rounded-2xl') return '1rem';
      if (inputBorderRadius === 'rounded-xl') return '0.75rem';
      if (inputBorderRadius === 'rounded-lg') return '0.5rem';
      if (inputBorderRadius === 'rounded-md') return '0.375rem';
      if (inputBorderRadius === 'rounded-sm') return '0.125rem';
      if (inputBorderRadius === 'rounded-full') return '9999px';
      // Gérer les variantes avec des nombres (rounded-[...])
      const match = inputBorderRadius.match(/rounded-\[(.+?)\]/);
      if (match) {
        return match[1];
      }
    }
    return '1rem'; // Default rounded-2xl
  };

  // Convertir la couleur en rgba pour le box-shadow
  const getFocusRingColor = () => {
    if (focusRing.startsWith('#')) {
      const hex = focusRing.replace('#', '');
      if (hex.length === 6) {
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 0.25)`;
      }
      return `${focusRing}40`;
    }
    if (focusRing.includes('rgba')) {
      return focusRing;
    }
    return `${focusRing}40`;
  };

  // Appliquer les styles de personnalisation aux éléments générés
  useEffect(() => {
    if (!containerRef.current) return;

    // Utiliser un MutationObserver pour s'assurer que les éléments sont créés
    const applyStyles = () => {
      const phoneInputElement = containerRef.current?.querySelector('.PhoneInput') as HTMLElement;
      if (!phoneInputElement) return;

      // Appliquer les styles au conteneur principal avec !important via setProperty
      phoneInputElement.style.setProperty('background-color', inputBg, 'important');
      phoneInputElement.style.setProperty('border-color', inputBorder, 'important');
      phoneInputElement.style.setProperty('border-width', '1px', 'important');
      phoneInputElement.style.setProperty('border-style', 'solid', 'important');
      phoneInputElement.style.setProperty('border-radius', getBorderRadiusStyle(), 'important');
      phoneInputElement.style.setProperty('color', inputText, 'important');

      // Appliquer les styles aux éléments internes
      const inputElement = phoneInputElement.querySelector('.PhoneInputInput') as HTMLInputElement;
      if (inputElement) {
        inputElement.style.setProperty('color', inputText, 'important');
        inputElement.style.setProperty('background-color', 'transparent', 'important');
        inputElement.style.setProperty('border', 'none', 'important');
        inputElement.style.setProperty('outline', 'none', 'important');
        inputElement.style.setProperty('width', '100%', 'important');

        // Forcer le style du placeholder via un attribut data et via CSS variables
        inputElement.setAttribute('data-placeholder-color', inputPlaceholder);
        inputElement.style.setProperty('--placeholder-color', inputPlaceholder, 'important');
      }

      const countrySelect = phoneInputElement.querySelector('.PhoneInputCountrySelect') as HTMLElement;
      if (countrySelect) {
        countrySelect.style.color = inputText;
        countrySelect.style.backgroundColor = 'transparent';
      }

      const callingCode = phoneInputElement.querySelector('.PhoneInputCountryCallingCode') as HTMLElement;
      if (callingCode) {
        callingCode.style.color = inputText;
        callingCode.style.backgroundColor = 'transparent';
      }

      // Appliquer les styles aux icônes
      const countryIcon = phoneInputElement.querySelector('.PhoneInputCountryIcon') as HTMLElement;
      if (countryIcon) {
        countryIcon.style.filter = 'none';
      }
    };

    // Appliquer immédiatement
    applyStyles();

    // Observer les changements dans le DOM
    const observer = new MutationObserver(() => {
      applyStyles();
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    // Gestion du focus
    const phoneInputElement = containerRef.current.querySelector('.PhoneInput') as HTMLElement;
    if (!phoneInputElement) {
      observer.disconnect();
      return;
    }

    const handleFocus = () => {
      if (!error && !(!isValid && value)) {
        phoneInputElement.style.setProperty('border-color', focusBorder, 'important');
        phoneInputElement.style.setProperty('box-shadow', `0 0 0 0.2rem ${getFocusRingColor()}`, 'important');
      }
    };

    const handleBlur = () => {
      if (!error && !(!isValid && value)) {
        phoneInputElement.style.setProperty('border-color', inputBorder, 'important');
        phoneInputElement.style.setProperty('box-shadow', '', 'important');
      }
    };

    phoneInputElement.addEventListener('focusin', handleFocus);
    phoneInputElement.addEventListener('focusout', handleBlur);

    return () => {
      observer.disconnect();
      phoneInputElement.removeEventListener('focusin', handleFocus);
      phoneInputElement.removeEventListener('focusout', handleBlur);
    };
  }, [inputBg, inputBorder, inputText, inputPlaceholder, inputBorderRadius, focusBorder, focusRing, error, isValid, value, textSecondary]);

  return (
    <div ref={containerRef} id={`phone-input-container-${uniqueId}`}>
      <PhoneInputWithCountry
        country={country as any || "FR"}
        onCountryChange={(c) => onCountryChange?.(c || "FR")}
        international={false}
        withCountryCallingCode={false}
        displayInitialValueAsLocalNumber={true}
        countryCallingCodeEditable={false}
        defaultCountry="FR"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`PhoneInput ${error ? "PhoneInput--error" : ""} ${!isValid && value ? "PhoneInput--invalid" : ""} ${disabled ? "PhoneInput--disabled" : ""}`}
      />

      {/* Styles CSS pour les éléments spécifiques */}
      <style>{`
        #phone-input-container-${uniqueId} .PhoneInput {
          background-color: ${inputBg} !important;
          border-color: ${inputBorder} !important;
          border-width: 1px !important;
          border-style: solid !important;
          border-radius: ${getBorderRadiusStyle()} !important;
          color: ${inputText} !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputInput {
          color: ${inputText} !important;
          background-color: transparent !important;
          --placeholder-color: ${inputPlaceholder} !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputInput::placeholder {
          color: ${inputPlaceholder} !important;
          opacity: 1 !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputInput::-webkit-input-placeholder {
          color: ${inputPlaceholder} !important;
          opacity: 1 !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputInput::-moz-placeholder {
          color: ${inputPlaceholder} !important;
          opacity: 1 !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputInput:-ms-input-placeholder {
          color: ${inputPlaceholder} !important;
          opacity: 1 !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputInput::placeholder-shown {
          color: ${inputPlaceholder} !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputCountrySelect {
          color: ${inputText} !important;
          background-color: transparent !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputCountrySelect:hover {
          background-color: ${textSecondary}15 !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputCountryCallingCode {
          color: ${inputText} !important;
          background-color: transparent !important;
        }
        #phone-input-container-${uniqueId} .PhoneInputCountryCallingCode:hover {
          background-color: ${textSecondary}15 !important;
        }
        #phone-input-container-${uniqueId} .PhoneInput:focus-within {
          border-color: ${focusBorder} !important;
          box-shadow: 0 0 0 0.2rem ${getFocusRingColor()} !important;
        }
        ${disabled ? `
        #phone-input-container-${uniqueId} .PhoneInput {
          opacity: 0.6 !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
        }
        ` : ''}
        ${error ? `
        #phone-input-container-${uniqueId} .PhoneInput {
          border-color: #DC2626 !important;
        }
        ` : ''}
        ${!isValid && value ? `
        #phone-input-container-${uniqueId} .PhoneInput {
          border-color: #DC2626 !important;
        }
        ` : ''}
      `}</style>

      {error && (
        <p
          className="mt-1 text-sm"
          style={{ color: '#DC2626' }}
        >
          {error}
        </p>
      )}
      {!isValid && value && !error && (
        <p
          className="mt-1 text-sm"
          style={{ color: '#DC2626' }}
        >
          Numéro de téléphone invalide
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
